import React from "react";

interface DialogStateVoid {
  open: boolean;
  openDialog: () => void;
  close: () => void;
}

interface DialogStateWithData<T> {
  open: boolean;
  data: T | undefined;
  openWith: (data: T) => void;
  close: () => void;
}

/**
 * Generic dialog state hook. Eliminates the repeated useState(false) + data pairs.
 *
 * Usage (no payload):
 *   const createDialog = useDialogState();
 *   createDialog.openDialog();
 *   <CreateDialog open={createDialog.open} onClose={createDialog.close} />
 *
 * Usage (with payload):
 *   const deleteDialog = useDialogState<{ id: string; title: string }>();
 *   deleteDialog.openWith({ id: "123", title: "My Meeting" });
 *   <DeleteDialog title={deleteDialog.data?.title} open={deleteDialog.open} onClose={deleteDialog.close} />
 */
export function useDialogState(): DialogStateVoid;
export function useDialogState<T>(): DialogStateWithData<T>;
export function useDialogState<T = undefined>(): DialogStateVoid | DialogStateWithData<T> {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState<T | undefined>(undefined);

  const openDialog = React.useCallback(() => setOpen(true), []);

  const openWith = React.useCallback((d: T) => {
    setData(d);
    setOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
    setData(undefined);
  }, []);

  return { open, data, openDialog, openWith, close } as DialogStateVoid & DialogStateWithData<T>;
}
